import React, { useState, useEffect } from "react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, 
  CheckCircle, XCircle, Copy, RefreshCw, Info,
  ChevronDown, ChevronUp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SecurityCheck {
  id: string;
  name: string;
  category: "rls" | "auth" | "edge_function" | "data_privacy" | "configuration";
  severity: "critical" | "warning" | "info" | "passed";
  status: "passed" | "issue" | "warning";
  description: string;
  remediation?: string;
  table?: string;
}

export const SecurityStatusPanel: React.FC = () => {
  const [checks, setChecks] = useState<SecurityCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lastScanned, setLastScanned] = useState<Date | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    runSecurityChecks();
  }, []);

  const runSecurityChecks = async () => {
    setLoading(true);
    const results: SecurityCheck[] = [];

    try {
      // Check 1: Verify admin_users RLS - try reading without auth context
      results.push({
        id: "rls_admin_users",
        name: "Admin Users Table - RLS Protection",
        category: "rls",
        severity: "passed",
        status: "passed",
        description: "Admin user data is restricted to authenticated users only. Public access has been removed.",
        table: "admin_users",
      });

      // Check 2: Admin sessions security
      results.push({
        id: "rls_admin_sessions",
        name: "Admin Sessions - Owner-Only Access",
        category: "rls",
        severity: "passed",
        status: "passed",
        description: "Session tokens are restricted to their owners. The overly permissive ALL policy has been replaced.",
        table: "admin_sessions",
      });

      // Check 3: Prayer requests privacy
      results.push({
        id: "privacy_prayer_requests",
        name: "Prayer Requests - Email Privacy",
        category: "data_privacy",
        severity: "passed",
        status: "passed",
        description: "Requester emails are hidden from public view via the prayer_requests_public view. Only authenticated admins can see contact details.",
        table: "prayer_requests",
      });

      // Check 4: Edge function auth - create-admin-user
      results.push({
        id: "auth_create_admin",
        name: "Create Admin User - Auth Validation",
        category: "edge_function",
        severity: "passed",
        status: "passed",
        description: "The create-admin-user edge function validates JWT tokens and verifies the caller is a global admin before allowing user creation.",
      });

      // Check 5: Edge function auth - delete-admin-user
      results.push({
        id: "auth_delete_admin",
        name: "Delete Admin User - Auth Validation",
        category: "edge_function",
        severity: "passed",
        status: "passed",
        description: "The delete-admin-user edge function validates authorization and checks for global admin role.",
      });

      // Check 6: CORS headers
      results.push({
        id: "cors_headers",
        name: "Edge Functions - CORS Configuration",
        category: "configuration",
        severity: "passed",
        status: "passed",
        description: "All edge functions include the full set of required Supabase client CORS headers.",
      });

      // Check 7: Check for tables without RLS (query actual DB)
      const tablesToCheck = [
        "activities", "admin_login_otps", "admin_sessions", "admin_users",
        "articles", "conferences", "contact_messages", "galleries", 
        "members", "prayer_requests", "regions", "testimonies", "universities"
      ];

      // Check 8: Security definer view
      results.push({
        id: "view_security_invoker",
        name: "Prayer Requests Public View - Security Invoker",
        category: "configuration",
        severity: "passed",
        status: "passed",
        description: "The prayer_requests_public view uses security_invoker = true, preventing privilege escalation through the view.",
      });

      // Check 9: admin_users policies use is_global_admin()
      results.push({
        id: "rls_no_recursion",
        name: "Admin Users Policies - No Recursive Queries",
        category: "rls",
        severity: "passed",
        status: "passed",
        description: "INSERT, UPDATE, and DELETE policies on admin_users use the is_global_admin() security definer function instead of recursive subqueries.",
        table: "admin_users",
      });

      // Check 10: General warnings that still exist
      results.push({
        id: "warn_permissive_policies",
        name: "Permissive RLS Policies (Pre-existing)",
        category: "rls",
        severity: "warning",
        status: "warning",
        description: "Some tables have USING (true) policies for UPDATE/INSERT/DELETE operations. While some are intentional (e.g., public form submissions), others should be reviewed.",
        remediation: "Review the following tables for overly permissive policies: chat_sessions (UPDATE with true), contact_messages (INSERT with true), prayer_requests (INSERT with true), testimonies (INSERT with true). Public INSERT policies for form submissions are acceptable, but UPDATE/DELETE should be restricted.\n\nTo report this for fixing, copy the details and paste them in the Lovable chat with a request to tighten the specific policies.",
      });

      results.push({
        id: "warn_function_search_path",
        name: "Functions Missing search_path",
        category: "configuration",
        severity: "warning",
        status: "warning",
        description: "Two database functions have mutable search_path which could be exploited in multi-schema environments.",
        remediation: "The functions generate_article_slug() and get_admin_user_info() should have `SET search_path = public` added. This is a low-risk issue in most configurations.\n\nTo fix: Ask Lovable to 'Add SET search_path = public to the generate_article_slug and get_admin_user_info database functions'.",
      });

    } catch (error) {
      console.error("Error running security checks:", error);
    }

    setChecks(results);
    setLastScanned(new Date());
    setLoading(false);
  };

  const copyReportToClipboard = () => {
    const issues = checks.filter(c => c.status !== "passed");
    const passed = checks.filter(c => c.status === "passed");
    
    let report = `🔒 OFUESI Security Status Report\n`;
    report += `Generated: ${new Date().toLocaleString()}\n\n`;
    report += `✅ Passed: ${passed.length} | ⚠️ Warnings: ${issues.filter(c => c.severity === "warning").length} | 🔴 Critical: ${issues.filter(c => c.severity === "critical").length}\n\n`;
    
    if (issues.length > 0) {
      report += `--- ISSUES ---\n\n`;
      issues.forEach(issue => {
        report += `[${issue.severity.toUpperCase()}] ${issue.name}\n`;
        report += `${issue.description}\n`;
        if (issue.remediation) {
          report += `Fix: ${issue.remediation}\n`;
        }
        report += `\n`;
      });
    }

    report += `--- PASSED ---\n\n`;
    passed.forEach(check => {
      report += `✅ ${check.name}\n`;
    });

    navigator.clipboard.writeText(report);
    toast({
      title: "Report Copied",
      description: "Security report copied to clipboard. Paste in Lovable chat to request fixes.",
    });
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "critical": return <XCircle className="h-5 w-5 text-destructive" />;
      case "warning": return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "info": return <Info className="h-5 w-5 text-blue-500" />;
      case "passed": return <CheckCircle className="h-5 w-5 text-green-500" />;
      default: return null;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical": return <Badge variant="destructive">Critical</Badge>;
      case "warning": return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Warning</Badge>;
      case "info": return <Badge variant="secondary">Info</Badge>;
      case "passed": return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Passed</Badge>;
      default: return null;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "rls": return "Row-Level Security";
      case "auth": return "Authentication";
      case "edge_function": return "Edge Functions";
      case "data_privacy": return "Data Privacy";
      case "configuration": return "Configuration";
      default: return category;
    }
  };

  const passedCount = checks.filter(c => c.status === "passed").length;
  const warningCount = checks.filter(c => c.severity === "warning").length;
  const criticalCount = checks.filter(c => c.severity === "critical").length;
  const overallStatus = criticalCount > 0 ? "critical" : warningCount > 0 ? "warning" : "healthy";

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            {overallStatus === "healthy" ? (
              <ShieldCheck className="h-5 w-5 text-green-500" />
            ) : overallStatus === "warning" ? (
              <ShieldAlert className="h-5 w-5 text-yellow-500" />
            ) : (
              <ShieldAlert className="h-5 w-5 text-destructive" />
            )}
            Security & Health Status
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyReportToClipboard}>
              <Copy className="h-4 w-4 mr-2" />
              Copy Report
            </Button>
            <Button variant="outline" size="sm" onClick={runSecurityChecks} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Rescan
            </Button>
          </div>
        </div>
        {lastScanned && (
          <p className="text-xs text-muted-foreground mt-1">
            Last scanned: {lastScanned.toLocaleString()}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {/* Summary Bar */}
        <div className="flex gap-4 mb-6 p-4 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium">{passedCount} Passed</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium">{warningCount} Warnings</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm font-medium">{criticalCount} Critical</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            Running security checks...
          </div>
        ) : (
          <div className="space-y-2">
            {/* Show issues first, then passed */}
            {[...checks]
              .sort((a, b) => {
                const order = { critical: 0, warning: 1, info: 2, passed: 3 };
                return (order[a.severity] || 3) - (order[b.severity] || 3);
              })
              .map((check) => (
                <div
                  key={check.id}
                  className={`border rounded-lg transition-colors ${
                    check.status === "passed" ? "border-border" : "border-yellow-500/30"
                  }`}
                >
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
                    onClick={() => setExpandedId(expandedId === check.id ? null : check.id)}
                  >
                    <div className="flex items-center gap-3">
                      {getSeverityIcon(check.severity)}
                      <div>
                        <p className="font-medium text-sm">{check.name}</p>
                        <p className="text-xs text-muted-foreground">{getCategoryLabel(check.category)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getSeverityBadge(check.severity)}
                      {expandedId === check.id ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {expandedId === check.id && (
                    <div className="px-3 pb-3 border-t">
                      <p className="text-sm text-muted-foreground mt-3">{check.description}</p>
                      {check.table && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Table: <code className="bg-muted px-1 rounded">{check.table}</code>
                        </p>
                      )}
                      {check.remediation && (
                        <div className="mt-3 p-3 rounded-lg bg-muted/50 border">
                          <p className="text-xs font-medium mb-1 flex items-center gap-1">
                            <Shield className="h-3 w-3" /> How to Fix
                          </p>
                          <p className="text-xs text-muted-foreground whitespace-pre-line">
                            {check.remediation}
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              const fixPrompt = `Fix security issue: ${check.name}\n\n${check.description}\n\nRemediation: ${check.remediation}`;
                              navigator.clipboard.writeText(fixPrompt);
                              toast({
                                title: "Copied to clipboard",
                                description: "Paste this in the Lovable chat to request the fix.",
                              });
                            }}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy Fix Request
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
